import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, CalendarRange, Download, AlertTriangle, Info,
  ArrowDown, ArrowUp, Scissors
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtPct = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
// Sütun başlığı: haftanın başlangıç günü + bitişi (Pzt–Paz)
const weekLabel = (key) => {
  try {
    const s = new Date(`${key}T00:00:00`);
    const e = new Date(s.getTime() + 6 * 86400000);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    return `${f(s)}–${f(e)}`;
  } catch { return key; }
};

const RANGES = [
  { key: 'all', label: 'Tüm haftalar' },
  { key: '12', label: 'Son 12 hafta' },
  { key: '8', label: 'Son 8 hafta' },
  { key: '4', label: 'Son 4 hafta' },
];
const CATS = ['Tümü', 'Ayakkabı', 'Tekstil', 'Aksesuar', 'Diğer'];
const SORTS = [
  { key: 'breakage_desc', label: 'Kırık (çoktan aza)' },
  { key: 'delta_desc', label: 'En çok kötüleşen' },
  { key: 'delta_asc', label: 'En çok iyileşen' },
  { key: 'critical_desc', label: 'Kritik ürün sayısı' },
  { key: 'products_desc', label: 'Ürün sayısı' },
  { key: 'brand_asc', label: 'Marka (A→Z)' },
];

// Kırık yüksekse kırmızı — %0 yeşilimsi, %75+ koyu kırmızı (Melissa eşiği)
const heat = (b) => {
  if (b == null) return { background: 'transparent', color: '#d1d5db' };
  const t = Math.max(0, Math.min(1, b / 75));
  return {
    background: `rgba(220, 38, 38, ${(0.06 + 0.82 * t).toFixed(3)})`,
    color: t > 0.5 ? '#fff' : '#7f1d1d',
  };
};

export default function High5SizeBreakageWeekly() {
  const { selectedBrand } = useAuth();
  const [rangeKey, setRangeKey] = useState('all');
  const [cat, setCat] = useState('Tümü');
  const [sort, setSort] = useState('breakage_desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/sku-feed/size-breakage-weekly?weeks=${rangeKey}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, rangeKey]);

  const columns = payload?.columns || [];
  const range = payload?.range || {};
  const method = payload?.method || {};
  const totals = payload?.totals || null;
  const allRows = useMemo(() => payload?.rows || [], [payload]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = allRows.filter((r) => {
      if (cat !== 'Tümü' && r.category !== cat) return false;
      if (q && !`${r.brand} ${r.category}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const cmp = {
      breakage_desc: (a, b) => (b.lastBreakage ?? -1) - (a.lastBreakage ?? -1),
      delta_desc: (a, b) => (b.delta ?? -999) - (a.delta ?? -999),
      delta_asc: (a, b) => (a.delta ?? 999) - (b.delta ?? 999),
      critical_desc: (a, b) => b.lastCritical - a.lastCritical,
      products_desc: (a, b) => b.lastProducts - a.lastProducts,
      brand_asc: (a, b) => String(a.brand).localeCompare(String(b.brand), 'tr')
        || String(a.category).localeCompare(String(b.category), 'tr'),
    }[sort];
    return [...out].sort(cmp);
  }, [allRows, cat, search, sort]);

  // Filtrelenen satırların hafta hafta ağırlıklı toplamı (basit ortalama DEĞİL —
  // evren/available toplanır, yoksa küçük markalar toplamı çarpıtır)
  const filteredTotals = useMemo(() => {
    const cells = {};
    columns.forEach((c) => { cells[c.key] = { u: 0, a: 0, p: 0, c: 0 }; });
    rows.forEach((r) => {
      columns.forEach((c) => {
        const cell = r.cells?.[c.key];
        if (!cell) return;
        cells[c.key].u += cell.u || 0;
        cells[c.key].a += cell.a || 0;
        cells[c.key].p += cell.p || 0;
        cells[c.key].c += cell.c || 0;
      });
    });
    Object.values(cells).forEach((v) => { v.b = v.u > 0 ? Math.round((1 - v.a / v.u) * 1000) / 10 : null; });
    return cells;
  }, [rows, columns]);

  const exportCsv = () => {
    const head = ['Marka', 'Kategori', 'Son hafta kirik %', 'Degisim (puan)', 'Urun', 'Kritik urun',
      ...columns.map((c) => `${c.key} kirik %`)];
    const lines = rows.map((r) => [
      r.brand, r.category, r.lastBreakage ?? '', r.delta ?? '', r.lastProducts, r.lastCritical,
      ...columns.map((c) => r.cells?.[c.key]?.b ?? ''),
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `beden-kirigi-haftalik_${range.start || ''}_${range.end || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center">
                <Scissors className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Haftalık Beden Kırığı</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Satır = marka × kategori, sütun = hafta, hücre = <strong>beden aralığının yüzde kaçı o hafta stokta değildi</strong>.
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={rangeKey} onChange={(e) => setRangeKey(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <button type="button" onClick={exportCsv} disabled={loading || rows.length === 0}
                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button type="button" onClick={fetchData} disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Kategori sekmeleri */}
          <div className="mt-5 flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                  cat === c ? 'bg-red-600 text-white border-red-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>
                {c}
              </button>
            ))}
          </div>

          {/* Özet */}
          <div className="mt-4 rounded-2xl border border-red-100 bg-gradient-to-r from-red-50 to-white p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-3">
                <CalendarRange className="w-5 h-5 text-red-600" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-red-700/70 font-semibold">Hafta aralığı</div>
                  <div className="text-lg font-bold text-gray-900">
                    {fmtDate(range.start)} <span className="text-gray-400 font-normal">→</span> {fmtDate(range.end)}
                    <span className="text-sm font-normal text-gray-400"> · {range.buckets || 0} hafta</span>
                  </div>
                </div>
              </div>
              <div className="h-8 w-px bg-red-100 hidden sm:block" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-red-700/70 font-semibold">Satır</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">
                  {fmtNumber(rows.length)} <span className="text-sm font-normal text-gray-400">marka × kategori</span>
                </div>
              </div>
              {totals && range.end && (
                <>
                  <div className="h-8 w-px bg-red-100 hidden sm:block" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-red-700/70 font-semibold">Son hafta kırık</div>
                    <div className="text-lg font-bold text-gray-900 tabular-nums">
                      {fmtPct(filteredTotals[range.end]?.b)}
                      <span className="text-sm font-normal text-gray-400">
                        {' '}· {fmtNumber(filteredTotals[range.end]?.c || 0)} kritik ürün (&gt;%75)
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Yöntem — bu sayfada şart, sayı tek başına yanıltır */}
          <div className="mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong>Beden aralığı</strong> = o üründe bugüne kadar görülmüş tüm bedenlerin birleşimi
              (MC tüm tarihçe + hebiar tam liste). <strong>Kırık</strong> = bu aralığın o hafta stokta
              olmayan kısmı.
              {' '}<strong>Yıldızlı (★) haftalar kesin sayım</strong> (hebiar); diğerleri
              <strong> tahmin</strong>: MC günde ürün başına tek beden gösterdiği için hafta içinde görülen
              farklı beden sayısından istatistiksel olarak geri hesaplanır.
              {method.biasPoints && (
                <> 🔴 <strong>Tahmin seviyeyi ~{String(method.biasPoints).replace('.', ',')} puan yukarı şişiriyor</strong> ({method.biasNote}).
                Haftalar arası <strong>trend</strong> güvenilir, mutlak seviye değil — kesin rakam için ★ haftalara bak.</>
              )}
              {' '}Feed 29 Haz 2026'ya kadar beden döndürmediği için öncesi hiç gösterilmiyor.
              Haftalar aynı ürün kohortu üzerinden hesaplanır, yoksa popülasyon değişimi sahte sıçrama yapar.
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Marka ara…" className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        <div className="p-0">
          {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
          {!selectedBrand?.id && <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>}
          {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}
          {!loading && !error && rows.length === 0 && selectedBrand?.id && (
            <div className="p-8 text-center text-gray-400">Kayıt yok.</div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div className="overflow-auto max-h-[70vh]">
              <table className="text-sm border-separate w-full" style={{ borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[210px]">
                      Marka × Kategori
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[74px]"
                        style={{ left: 210 }} title="Son haftanın kırık oranı">
                      Son
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[66px]"
                        style={{ left: 284 }} title="İlk haftadan son haftaya değişim (puan) — artı = kötüleşme">
                      Δ
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} title={`${fmtDate(c.key)} haftası${c.exact ? ' · kesin sayım (hebiar)' : ' · tahmin'}`}
                          className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-[11px] font-semibold text-gray-500 whitespace-nowrap min-w-[76px]">
                        {weekLabel(c.key)}{c.exact && <span className="text-emerald-600"> ★</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Filtrelenen satırların ağırlıklı toplamı */}
                  <tr className="font-semibold">
                    <td className="sticky left-0 z-20 bg-red-50 border-b border-r border-red-200 px-3 py-2">
                      <div className="text-gray-900">TOPLAM {cat !== 'Tümü' ? `· ${cat}` : ''}</div>
                      <div className="text-[10px] text-gray-500 font-normal">
                        {fmtNumber(filteredTotals[range.end]?.p || 0)} ürün · evren ağırlıklı
                      </div>
                    </td>
                    <td className="sticky z-10 bg-red-50 border-b border-red-200 px-2 py-2 text-center tabular-nums text-gray-900" style={{ left: 210 }}>
                      {fmtPct(filteredTotals[range.end]?.b)}
                    </td>
                    <td className="sticky z-10 bg-red-50 border-b border-r border-red-200 px-2 py-2 text-center text-gray-400" style={{ left: 284 }}>—</td>
                    {columns.map((c) => {
                      const v = filteredTotals[c.key];
                      return (
                        <td key={c.key}
                            title={`${fmtDate(c.key)} · ${fmtPct(v?.b)} kırık · ${fmtNumber(v?.a || 0)}/${fmtNumber(v?.u || 0)} beden stokta · ${fmtNumber(v?.c || 0)} kritik ürün`}
                            className="border-b border-red-200 px-1 py-2 text-center tabular-nums text-xs font-bold cursor-default"
                            style={heat(v?.b)}>
                          {v?.b == null ? '·' : fmtPct(v.b)}
                        </td>
                      );
                    })}
                  </tr>
                  {rows.map((r) => (
                    <tr key={`${r.brand}||${r.category}`} className="hover:bg-red-50/20">
                      <td className="sticky left-0 z-20 bg-white border-b border-r border-gray-100 px-3 py-1.5">
                        <div className="font-medium text-gray-800 text-xs">{r.brand}</div>
                        <div className="text-[10px] text-gray-400">
                          {r.category} · {fmtNumber(r.lastProducts)} ürün
                          {r.lastCritical > 0 && <span className="text-red-600"> · {r.lastCritical} kritik</span>}
                        </div>
                      </td>
                      <td className="sticky z-10 bg-white border-b border-gray-100 px-2 py-1.5 text-center tabular-nums text-xs font-bold" style={{ left: 210 }}>
                        <span className={r.lastBreakage > 75 ? 'text-red-700' : 'text-gray-800'}>
                          {fmtPct(r.lastBreakage)}
                        </span>
                      </td>
                      <td className="sticky z-10 bg-white border-b border-r border-gray-100 px-2 py-1.5 text-center" style={{ left: 284 }}>
                        {r.delta == null ? <span className="text-gray-300">—</span> : (
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold tabular-nums ${
                            r.delta > 0 ? 'bg-red-100 text-red-700' : r.delta < 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {r.delta > 0 ? <ArrowUp className="w-3 h-3" /> : r.delta < 0 ? <ArrowDown className="w-3 h-3" /> : null}
                            {r.delta > 0 ? '+' : ''}{String(r.delta).replace('.', ',')}
                          </span>
                        )}
                      </td>
                      {columns.map((c) => {
                        const cell = r.cells?.[c.key];
                        return (
                          <td key={c.key}
                              title={cell
                                ? `${fmtDate(c.key)} · ${fmtPct(cell.b)} kırık · ${fmtNumber(cell.a)}/${fmtNumber(cell.u)} beden stokta · ${fmtNumber(cell.p)} ürün · ${fmtNumber(cell.c)} kritik · ${cell.e ? 'kesin sayım' : 'tahmin'}`
                                : `${fmtDate(c.key)} · veri yok`}
                              className={`border-b border-gray-100 px-1 py-1.5 text-center tabular-nums text-xs font-semibold cursor-default${cell?.e ? ' ring-1 ring-inset ring-emerald-500' : ''}`}
                              style={heat(cell?.b)}>
                            {cell?.b == null ? '·' : fmtPct(cell.b)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-white flex items-start gap-3 text-xs text-gray-500">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Kritik ürün</strong> = beden kırığı %75'in üstünde olan ürün (ajans kuralı:
            “Variant Availability Score &lt; 0,25 exclude”). Hücreye gelince o haftanın evren/stok
            kırılımı ve kritik ürün sayısı görünür.
          </div>
        </div>
      </Card>
    </div>
  );
}
